/**
 * DOCUMENTATION:
 * Component: LangChainBasics
 * Purpose: Teaching the 'Orchestration' layer of AI using LangChain.js.
 * * * Note for Junior Developers:
 * - Prompt Template: A blueprint for the AI's instructions.
 * - Chain: A sequence of actions (e.g., Fetch Data -> Format Prompt -> Call AI).
 * - Client-Side AI: We are running this logic in the browser to save costs.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Button, styled, Stack, TextField, CircularProgress, Divider
} from "@mui/material";

/* Icons */
import HubIcon from '@mui/icons-material/Hub';
import ExtensionIcon from '@mui/icons-material/Extension';
import SmartToyIcon from '@mui/icons-material/SmartToy';

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
    color: "white",
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(30, 20, 50, 0.8)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: "1px solid rgba(167, 139, 250, 0.2)",
    color: "white",
    marginBottom: theme.spacing(3),
}));

export default function LangChainBasics() {
    const [input, setInput] = useState("");
    const [response, setResponse] = useState("");
    const [loading, setLoading] = useState(false);

    // Simulation of a LangChain 'Chain' in the browser
    const runSimulatedChain = async () => {
        if (!input) return;
        setLoading(true);

        // Step 1: Prompt Template Logic
        const template = `Answer the following as a ProDev Mentor: ${input}`;

        // Step 2: Simulated LLM Call (In reality, this calls Gemini SDK)
        setTimeout(() => {
            setResponse(`[AI Mentor]: I've processed your request using the template: "${template}". In a real RAG app, I would now pull lesson context from Firestore.`);
            setLoading(false);
        }, 1500);
    };

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant="h3" fontWeight={900} color="#a78bfa">
                    LANGCHAIN ORCHESTRATION
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.7 }}>
                    Connecting the Pieces of the AI Puzzle
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                {/* 1. Core Concepts */}
                <Grid item xs={12} md={6}>
                    <GlassCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <HubIcon sx={{ color: "#a78bfa" }} />
                            <Typography variant="h5" fontWeight={800}>What is LangChain?</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8, mb: 3 }}>
                            LangChain is a framework that allows you to "chain" different AI components together.
                            Instead of just sending a message, you can create complex workflows.
                        </Typography>



                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#a78bfa">Models</Typography>
                                <Typography variant="caption">The "Engine" (Gemini, Llama, GPT).</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#a78bfa">Prompts</Typography>
                                <Typography variant="caption">The instructions and formatting for the Model.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#a78bfa">Chains</Typography>
                                <Typography variant="caption">The logic that links Models and Prompts together.</Typography>
                            </Box>
                        </Stack>
                    </GlassCard>
                </Grid>

                {/* 2. Live Sandbox */}
                <Grid item xs={12} md={6}>
                    <GlassCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SmartToyIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Chain Sandbox</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                            Test a basic "Instruction + Input" chain. This happens entirely in your browser.
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            placeholder="Ask a question about System Design..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            sx={{
                                mb: 2,
                                bgcolor: "rgba(0,0,0,0.2)",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-root": { color: "white", "& fieldset": { borderColor: "rgba(167, 139, 250, 0.3)" } }
                            }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={runSimulatedChain}
                            disabled={loading}
                            sx={{ bgcolor: "#a78bfa", color: "#0a0519", fontWeight: 800 }}
                        >
                            {loading ? <CircularProgress size={24} /> : "RUN CHAIN"}
                        </Button>

                        {response && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(167, 139, 250, 0.1)", borderRadius: "8px", borderLeft: "4px solid #a78bfa" }}>
                                <Typography variant="body2">{response}</Typography>
                            </Box>
                        )}
                    </GlassCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/ai-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#a78bfa", color: "#0a0519", fontWeight: 800, px: 6, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
